import React, { useEffect, useLayoutEffect, useState } from "react";
import { Button } from "../../../../design-system";
import { useTour } from "./TourContext";
import classes from "./Tour.module.less";

interface Rect {
    top: number;
    left: number;
    width: number;
    height: number;
}

const PADDING = 8;
const CARD_GAP = 16;
const CARD_WIDTH = 380;

function placeCard(rect: Rect): React.CSSProperties {
    // Prefer right; fall back to below; clamp to viewport.
    const w = window.innerWidth;
    const h = window.innerHeight;
    let top = rect.top;
    let left = rect.left + rect.width + CARD_GAP;

    if (left + CARD_WIDTH > w - 16) {
        left = rect.left;
        top = rect.top + rect.height + CARD_GAP;
    }
    if (top + 280 > h - 16) {
        top = Math.max(16, h - 280 - 16);
    }
    if (top < 16) top = 16;
    if (left < 16) left = 16;
    return { top, left };
}

export const Tour: React.FC = () => {
    const tour = useTour();
    const [rect, setRect] = useState<Rect | null>(null);

    const currentStep = tour.steps[tour.step];

    useLayoutEffect(() => {
        if (!tour.active) {
            setRect(null);
            return;
        }
        let raf = 0;
        const tryMeasure = (attempt = 0) => {
            const el = document.querySelector<HTMLElement>(`[data-tour-id="${currentStep.anchorId}"]`);
            if (!el) {
                if (attempt < 30) {
                    raf = window.setTimeout(() => tryMeasure(attempt + 1), 60) as unknown as number;
                }
                return;
            }
            el.scrollIntoView({ behavior: "smooth", block: "center" });
            // Measure after the smooth-scroll settles.
            window.setTimeout(() => {
                const r = el.getBoundingClientRect();
                setRect({ top: r.top - PADDING, left: r.left - PADDING, width: r.width + PADDING * 2, height: r.height + PADDING * 2 });
            }, 350);
        };
        tryMeasure();
        return () => window.clearTimeout(raf);
    }, [tour.active, tour.step, currentStep]);

    useEffect(() => {
        if (!tour.active) return;
        const onResize = () => {
            const el = document.querySelector<HTMLElement>(`[data-tour-id="${currentStep.anchorId}"]`);
            if (!el) return;
            const r = el.getBoundingClientRect();
            setRect({
                top: r.top - PADDING,
                left: r.left - PADDING,
                width: r.width + PADDING * 2,
                height: r.height + PADDING * 2,
            });
        };
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") tour.end();
            if (e.key === "ArrowRight") tour.next();
            if (e.key === "ArrowLeft") tour.prev();
        };
        window.addEventListener("resize", onResize);
        window.addEventListener("scroll", onResize, true);
        window.addEventListener("keydown", onKey);
        return () => {
            window.removeEventListener("resize", onResize);
            window.removeEventListener("scroll", onResize, true);
            window.removeEventListener("keydown", onKey);
        };
    }, [tour.active, currentStep, tour]);

    if (!tour.active) return null;

    return (
        <>
            <div className={classes.scrim} onClick={() => tour.end()} />
            {rect && (
                <div
                    className={classes.spotlight}
                    style={{
                        top: rect.top,
                        left: rect.left,
                        width: rect.width,
                        height: rect.height,
                    }}
                />
            )}
            <div
                className={classes.card}
                style={
                    rect
                        ? placeCard(rect)
                        : { top: "40%", left: "50%", transform: "translate(-50%, -50%)" }
                }
            >
                <div className={classes.step}>
                    Step {tour.step + 1} of {tour.steps.length}
                </div>
                <div className={classes.title}>{currentStep.title}</div>
                <div className={classes.text}>{currentStep.text}</div>
                <div className={classes.controls}>
                    <div className={classes.dots}>
                        {tour.steps.map((_, i) => (
                            <span key={i} className={`${classes.dot} ${i === tour.step ? classes.on : ""}`} />
                        ))}
                    </div>
                    <div className={classes.buttons}>
                        {tour.step > 0 && (
                            <Button size="small" onClick={() => tour.prev()}>
                                Back
                            </Button>
                        )}
                        <Button
                            type="primary"
                            size="small"
                            onClick={() => (tour.step === tour.steps.length - 1 ? tour.end() : tour.next())}
                        >
                            {tour.step === tour.steps.length - 1 ? "Done" : "Next"}
                        </Button>
                    </div>
                </div>
            </div>
        </>
    );
};
